import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Send, Image, User, ShieldCheck, CheckCheck, Paperclip, X } from 'lucide-react';
import { enviarMensagemChat, escutarMensagensChat } from '../services/chatService';
import type { MensagemCorridaFirestore } from '../types/firestore';

interface RealtimeChatProps {
  corridaId: string;
  remetenteId: string;
  tipoUsuario: 'cliente' | 'operador';
}

export function RealtimeChat({ corridaId, remetenteId, tipoUsuario }: RealtimeChatProps) {
  const [mensagens, setMensagens] = useState<MensagemCorridaFirestore[]>([]);
  const [texto, setTexto] = useState('');
  const [imagemUrl, setImagemUrl] = useState('');
  const [showImageInput, setShowImageInput] = useState(false);
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribe = escutarMensagensChat(corridaId, (msgs) => {
      setMensagens(msgs);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    });
    return () => unsubscribe();
  }, [corridaId]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!texto.trim() && !imagemUrl.trim()) return;

    try {
      setSending(true);
      await enviarMensagemChat(corridaId, {
        remetente_id: remetenteId,
        tipo_usuario: tipoUsuario,
        texto: texto.trim(),
        imagem_url: imagemUrl.trim()
      });

      setTexto('');
      setImagemUrl('');
      setShowImageInput(false);
    } catch (_e) {
      alert('Erro ao enviar mensagem no chat.');
    } finally {
      setSending(false);
    }
  };

  const formatHora = (timestampAny: any) => {
    if (!timestampAny) return '';
    let date: Date;
    if (timestampAny.toDate) {
      date = timestampAny.toDate();
    } else if (timestampAny.seconds) {
      date = new Date(timestampAny.seconds * 1000);
    } else {
      date = new Date(timestampAny);
    }
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden flex flex-col h-[480px] shadow-2xl">
      {/* Topo Chat */}
      <div className="bg-slate-950 p-4 border-b border-slate-800 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-brand-green/20 text-brand-green flex items-center justify-center font-bold">
            {tipoUsuario === 'cliente' ? <ShieldCheck size={18} /> : <User size={18} />}
          </div>
          <div>
            <h4 className="text-sm font-extrabold text-white">
              {tipoUsuario === 'cliente' ? 'Central de Atendimento / Operador' : 'Cliente (Chat Direto)'}
            </h4>
            <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
              Chat em Tempo Real (#{(corridaId || '').slice(-6).toUpperCase()})
            </span>
          </div>
        </div>
      </div>

      {/* Lista de Mensagens */}
      <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:16px_16px]">
        {mensagens.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-500 text-xs italic">
            <p>Nenhuma mensagem trocada ainda.</p>
            <p className="text-[10px] mt-1 text-slate-600">Escreva uma mensagem ou envie comprovantes abaixo.</p>
          </div>
        ) : (
          mensagens.map((msg, index) => {
            const isMe = msg.tipo_usuario === tipoUsuario;
            return (
              <motion.div
                key={msg.id || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
              >
                <div
                  className={`max-w-[82%] sm:max-w-[70%] p-3.5 rounded-2xl text-xs space-y-1.5 shadow-md ${
                    isMe
                      ? 'bg-brand-blue text-white rounded-br-none'
                      : 'bg-slate-800 text-slate-100 rounded-bl-none border border-slate-700'
                  }`}
                >
                  {/* Badge de identificação do remetente */}
                  <div className="flex items-center justify-between gap-2 border-b border-white/10 pb-1 text-[10px] font-bold text-white/70">
                    <span>{msg.tipo_usuario === 'operador' ? 'Operador (Central)' : 'Cliente'}</span>
                    <span>{formatHora(msg.enviado_em)}</span>
                  </div>

                  {/* Texto */}
                  {msg.texto && <p className="leading-relaxed font-medium break-words">{msg.texto}</p>}

                  {/* Imagem/Comprovante se houver */}
                  {msg.imagem_url && (
                    <div className="pt-1">
                      <a href={msg.imagem_url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={msg.imagem_url}
                          alt="Anexo"
                          className="max-h-48 rounded-xl object-cover border border-white/20 hover:opacity-90 transition-opacity"
                        />
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Caixa de Entrada */}
      <form onSubmit={handleSend} className="bg-slate-950 p-3 border-t border-slate-800 space-y-2">
        {showImageInput && (
          <div className="flex items-center gap-2 bg-slate-900 p-2 rounded-xl border border-slate-800">
            <Paperclip size={14} className="text-slate-400 shrink-0" />
            <input
              type="url"
              value={imagemUrl}
              onChange={(e) => setImagemUrl(e.target.value)}
              placeholder="Cole o link/URL da imagem ou comprovante..."
              className="w-full bg-transparent text-xs text-white outline-none placeholder:text-slate-500"
            />
            <button
              type="button"
              onClick={() => {
                setImagemUrl('');
                setShowImageInput(false);
              }}
              className="text-slate-400 hover:text-white"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowImageInput(!showImageInput)}
            className={`p-2.5 rounded-xl border transition-colors ${
              showImageInput
                ? 'bg-brand-blue border-brand-blue text-white'
                : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
            }`}
            title="Anexar imagem ou comprovante"
          >
            <Image size={18} />
          </button>

          <input
            type="text"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Digite sua mensagem em tempo real..."
            className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-brand-blue placeholder:text-slate-500"
          />

          <button
            type="submit"
            disabled={sending || (!texto.trim() && !imagemUrl.trim())}
            className="p-2.5 bg-brand-green hover:bg-emerald-700 text-slate-950 rounded-xl font-bold transition-all disabled:opacity-50"
          >
            <Send size={18} />
          </button>
        </div>
      </form>
    </div>
  );
}
